/**
 * combo-wizard.js
 * Prime Market Combo Special Wizard
 *
 * Handles:
 *  - sessionStorage state (blade, FH rubber, BH rubber)
 *  - Blade variant form submission → navigate to FH collection
 *  - Rubber collection search + sort (live filter)
 *  - Rubber PDP variant picker (syncs options → variant id + pricing)
 *  - Rubber PDP form submission → save to state → navigate
 *  - Review page: render summary, calculate surcharges, add 4 items to cart
 *  - Mini-summary bar on collection + PDP steps
 *  - Variant tile selection highlight
 */

(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────────────────────
  var STATE_KEY = 'cw_state';
  var ALLOWANCE_CENTS = 5500; // $55.00

  // ─── Utilities ────────────────────────────────────────────────────────────
  function getState() {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function mergeState(patch) {
    var s = getState();
    Object.assign(s, patch);
    sessionStorage.setItem(STATE_KEY, JSON.stringify(s));
  }

  function clearState() {
    sessionStorage.removeItem(STATE_KEY);
  }

  function surchargeFromCents(priceCents) {
    return Math.max(0, priceCents - ALLOWANCE_CENTS);
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ─── Variant tile highlight ────────────────────────────────────────────────
  function initVariantTileHighlight() {
    document.querySelectorAll('.cw-variant-tile input[type="radio"]').forEach(function (radio) {
      // Set initial state
      if (radio.checked) {
        radio.closest('.cw-variant-tile').classList.add('is-selected');
      }
      radio.addEventListener('change', function () {
        var group = this.closest('.cw-variants__grid');
        if (!group) return;
        group.querySelectorAll('.cw-variant-tile').forEach(function (tile) {
          tile.classList.remove('is-selected');
        });
        this.closest('.cw-variant-tile').classList.add('is-selected');
      });
    });
  }

  // ─── Mini summary bar ─────────────────────────────────────────────────────
  function renderMiniSummary() {
    var el = document.getElementById('cw-mini-summary');
    if (!el) return;

    var s = getState();
    var parts = [];

    if (s.bladeTitle) {
      parts.push('<span><strong>Blade:</strong> ' + s.bladeTitle + (s.bladeVariantTitle && s.bladeVariantTitle !== 'Default' ? ' — ' + s.bladeVariantTitle : '') + '</span>');
    }
    if (s.fhTitle) {
      parts.push('<span><strong>Forehand:</strong> ' + s.fhTitle + (s.fhVariantTitle && s.fhVariantTitle !== 'Default' ? ' — ' + s.fhVariantTitle : '') + '</span>');
    }
    if (s.bhTitle) {
      parts.push('<span><strong>Backhand:</strong> ' + s.bhTitle + (s.bhVariantTitle && s.bhVariantTitle !== 'Default' ? ' — ' + s.bhVariantTitle : '') + '</span>');
    }

    if (parts.length > 0) {
      el.innerHTML = '<div class="cw-mini-summary__inner">' + parts.join('<span class="cw-mini-summary__sep">·</span>') + '</div>';
      el.style.display = '';
    }
  }

  // ─── Step 1: Blade Form ────────────────────────────────────────────────────
  function initBladeForm() {
    var form = document.getElementById('cw-blade-form');
    if (!form) return;

    var comboHandle = form.getAttribute('data-combo-handle');
    var bladeProductId = form.getAttribute('data-blade-product-id');
    var bladeProductTitle = form.getAttribute('data-blade-product-title');
    var bladeHandle = form.getAttribute('data-blade-handle') || '';

    // Validate state: if combo handle changed, clear
    var s = getState();
    if (s.comboHandle && s.comboHandle !== comboHandle) {
      clearState();
    }
    mergeState({ comboHandle: comboHandle });

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var variantInput = form.querySelector('input[name="variant_id"]:checked') || form.querySelector('input[name="variant_id"]');
      if (!variantInput) {
        alert('Please select a handle variant to continue.');
        return;
      }

      var variantId = variantInput.value;
      var variantTitle = variantInput.getAttribute('data-variant-title') || '';

      mergeState({
        comboHandle: comboHandle,
        bladeProductId: bladeProductId,
        bladeHandle: bladeHandle,
        bladeTitle: bladeProductTitle,
        bladeVariantId: variantId,
        bladeVariantTitle: variantTitle,
        // Clear downstream selections since blade was re-chosen
        fhProductId: null,
        fhTitle: null,
        fhVariantId: null,
        fhVariantTitle: null,
        fhPriceCents: null,
        fhSurchargeCents: null,
        bhProductId: null,
        bhTitle: null,
        bhVariantId: null,
        bhVariantTitle: null,
        bhPriceCents: null,
        bhSurchargeCents: null
      });

      var baseUrl = form.getAttribute('data-base-url') || '';
      window.location.href = baseUrl + '?step=fh';
    });
  }

  // ─── Step 2 / 4: Rubber Collection — JS-driven from embedded JSON ───────────
  function initRubberCollection() {
    // Mobile filter toggle — simple in-flow show/hide, no overlay
    var mobileToggle = document.getElementById('cw-filters-mobile-toggle');
    var filterSidebar = document.getElementById('cw-filters-sidebar');

    if (mobileToggle && filterSidebar) {
      mobileToggle.addEventListener('click', function() {
        var isOpen = filterSidebar.classList.toggle('is-open');
        mobileToggle.innerHTML = isOpen
          ? '<span>✕ Close Filters</span><span>▲</span>'
          : '<span>☰ Filter Rubbers</span><span>▼</span>';
      });
    }
    var table = document.getElementById('cw-rubber-grid');
    if (!table) return;

    renderMiniSummary();

    // Derive step from URL (not static attributes — v129 renders all steps at once)
    var pageUrl = new URL(window.location.href);
    var currentStep = pageUrl.searchParams.get('step') || 'fh';
    var isFh = currentStep === 'fh';
    var cwStep = currentStep;
    var pdpStep = isFh ? 'fh_pdp' : 'bh_pdp';

    var baseUrl = table.getAttribute('data-base-url') || window.location.pathname;
    var comboHandle = table.getAttribute('data-combo-handle') || '';

    // BH guard: can't pick backhand before forehand is chosen
    var s = getState();
    if (currentStep === 'bh' && !s.fhVariantId) {
      window.location.href = baseUrl + '?step=fh';
      return;
    }

    var tbody        = document.getElementById('cw-rubber-tbody');
    var filterGroupsEl = document.getElementById('cw-filter-groups');
    var searchEl     = document.getElementById('cw-rubber-search');
    var emptyEl      = document.getElementById('cw-rubber-empty');
    var countEl      = document.getElementById('cw-result-count');
    var clearBtn     = document.getElementById('cw-filters-clear');

    var allRows = [];
    var activeFilters = { brand: [], type: [], tech: [], hardness: [], addon: [], speedMin: 0, speedMax: 100, spinMin: 0, spinMax: 100, controlMin: 0, controlMax: 100 };
    var sortCol = null;
    var sortDir = 'desc';
    var ALLOWANCE = 5500;

    // ── READ EMBEDDED JSON ────────────────────────────────────────
    var dataEl = document.getElementById('cw-all-rubber-data');
    if (!dataEl) {
      if (tbody) tbody.innerHTML = '<tr><td colspan="9" style="padding:2rem;text-align:center;color:#dc2626;">Could not load rubber data. Please refresh.</td></tr>';
      return;
    }

    var products = [];
    try { products = JSON.parse(dataEl.textContent); } catch(e) {
      if (tbody) tbody.innerHTML = '<tr><td colspan="9" style="padding:2rem;text-align:center;color:#dc2626;">Data parse error. Please refresh.</td></tr>';
      return;
    }

    // ── BUILD ROW ─────────────────────────────────────────────────
    function buildRow(p) {
      var price     = Number(p.price) || 0;          // already in cents from Liquid
      var surcharge = Math.max(0, price - ALLOWANCE);
      var speed     = String(p.speed    || '').trim();
      var spin      = String(p.spin     || '').trim();
      var control   = String(p.control  || '').trim();
      var hardness  = String(p.hardness || '').trim();
      var rtype     = String(p.rubber_type || '').trim();
      var rtech     = String(p.rubber_tech || '').trim();
      var imgUrl    = String(p.imgUrl   || '').trim();
      var id        = String(p.id       || '');
      var href      = baseUrl + '?step=' + pdpStep + '&position=' + cwStep + '&rubber_id=' + id + '&rubber_handle=' + encodeURIComponent(p.handle || '');

      function statCell(val) {
        if (!val) return '<td class="cw-col-stat">—</td>';
        return '<td class="cw-col-stat"><div class="cw-tbl-stat"><div class="cw-tbl-stat__bar"><div class="cw-tbl-stat__fill" style="width:' + val + '%"></div></div><span class="cw-tbl-stat__val">' + val + '</span></div></td>';
      }

      var addonCell = surcharge === 0
        ? '<td class="cw-col-addon"><span class="cw-badge-included">Included</span></td>'
        : '<td class="cw-col-addon"><span class="cw-badge-addon">+' + formatMoney(surcharge) + '</span></td>';

      var imgHtml = imgUrl
        ? '<a href="' + href + '" class="cw-compare-img-link" tabindex="-1"><img src="' + imgUrl + '" loading="eager" decoding="async" alt="' + (p.title||'').replace(/"/g,'&quot;') + '" class="cw-compare-thumb"></a>'
        : '<div class="cw-compare-thumb cw-compare-thumb--empty"></div>';

      var tr = document.createElement('tr');
      tr.className = 'cw-compare-row';
      tr.setAttribute('data-product-title', (p.title||'').toLowerCase());
      tr.setAttribute('data-vendor',  (p.vendor||'').toLowerCase());
      tr.setAttribute('data-type',    rtype.toLowerCase());
      tr.setAttribute('data-tech',    rtech.toLowerCase());
      tr.setAttribute('data-hardness',hardness.toLowerCase());
      tr.setAttribute('data-speed',   speed  || '0');
      tr.setAttribute('data-spin',    spin   || '0');
      tr.setAttribute('data-control', control|| '0');
      tr.setAttribute('data-price',   price);
      tr.setAttribute('data-surcharge', surcharge);
      tr.setAttribute('data-addon-type', surcharge === 0 ? 'included' : 'addon');

      tr.innerHTML = [
        '<td class="cw-col-img">' + imgHtml + '</td>',
        '<td class="cw-col-name"><a href="' + href + '" class="cw-compare-name-link">',
          '<span class="cw-compare-vendor">' + (p.vendor||'') + '</span>',
          '<span class="cw-compare-title">'  + (p.title ||'') + '</span>',
        '</a></td>',
        '<td class="cw-col-type">'  + (rtype   ||'—') + '</td>',
        '<td class="cw-col-tech">'  + (rtech   ||'—') + '</td>',
        '<td class="cw-col-hard">'  + (hardness||'—') + '</td>',
        statCell(speed),
        statCell(spin),
        statCell(control),
        addonCell,
        ''  // action column removed — clicking the row name/image navigates
      ].join('');

      // Make entire row clickable
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', function(e) {
        if (e.target.closest('a, button, input')) return;
        window.location.href = href;
      });
      return tr;
    }

    // ── FILTER SIDEBAR ────────────────────────────────────────────
    function buildFilters() {
      var brands = [], types = [], techs = [], hards = [];
      products.forEach(function(p) {
        var b = (p.vendor||'').trim(), t = (p.rubber_type||'').trim(), tc = (p.rubber_tech||'').trim(), h = (p.hardness||'').trim();
        if (b  && !brands.includes(b))  brands.push(b);
        if (t  && !types.includes(t))   types.push(t);
        if (tc && !techs.includes(tc))  techs.push(tc);
        if (h  && !hards.includes(h))   hards.push(h);
      });
      brands.sort();

      var typeOrder = ['Smooth','Short Pips','Medium Pips','Long Pips','Anti Spin','Hardbat'];
      types.sort(function(a,b){
        var ai = typeOrder.findIndex(function(v){return v.toLowerCase()===a.toLowerCase();});
        var bi = typeOrder.findIndex(function(v){return v.toLowerCase()===b.toLowerCase();});
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      });

      var techOrder = ['Tension','Hybrid','Sticky','Classic'];
      techs.sort(function(a,b){
        var ai = techOrder.findIndex(function(v){return v.toLowerCase()===a.toLowerCase();});
        var bi = techOrder.findIndex(function(v){return v.toLowerCase()===b.toLowerCase();});
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      });

      var hardOrder = ['Hard','Medium-Hard','Medium','Medium-Soft','Soft','N/A'];
      hards.sort(function(a,b){
        var ai = hardOrder.findIndex(function(v){return v.toLowerCase()===a.toLowerCase();});
        var bi = hardOrder.findIndex(function(v){return v.toLowerCase()===b.toLowerCase();});
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      });

      function checkboxGroup(label, filterKey, values) {
        if (!values.length) return '';
        return '<div class="cw-filter-group">' +
          '<button class="cw-filter-group__toggle" aria-expanded="false" type="button">' + label + ' <span class="cw-filter-group__arrow">▸</span></button>' +
          '<div class="cw-filter-group__body" style="display:none">' +
          values.map(function(v) {
            return '<label class="cw-filter-check"><input type="checkbox" value="' + v.replace(/"/g,'&quot;') + '" class="cw-filter-input" data-filter="' + filterKey + '"><span>' + v + '</span></label>';
          }).join('') + '</div></div>';
      }

      function sliderGroup(label, filterKey) {
        return '<div class="cw-filter-group">' +
          '<button class="cw-filter-group__toggle" aria-expanded="false" type="button">' + label + ' <span class="cw-filter-group__arrow">▸</span></button>' +
          '<div class="cw-filter-group__body" style="display:none">' +
            '<div class="cw-num-range" data-filter-key="' + filterKey + '">' +
              '<div class="cw-num-range__row">' +
                '<label class="cw-num-range__label">Min</label>' +
                '<input type="number" class="cw-num-input" id="cw-' + filterKey + '-min" min="0" max="100" value="" placeholder="0" data-filter="' + filterKey + 'Min">' +
              '</div>' +
              '<div class="cw-num-range__row">' +
                '<label class="cw-num-range__label">Max</label>' +
                '<input type="number" class="cw-num-input" id="cw-' + filterKey + '-max" min="0" max="100" value="" placeholder="100" data-filter="' + filterKey + 'Max">' +
              '</div>' +
            '</div>' +
          '</div></div>';
      }

      var html =
        checkboxGroup('Brand',      'brand',    brands) +
        checkboxGroup('Type',       'type',     types)  +
        checkboxGroup('Technology', 'tech',     techs)  +
        checkboxGroup('Hardness',   'hardness', hards)  +
        sliderGroup('Speed',   'speed')   +
        sliderGroup('Spin',    'spin')    +
        sliderGroup('Control', 'control') +
        '<div class="cw-filter-group">' +
          '<button class="cw-filter-group__toggle" aria-expanded="false" type="button">Add-On Cost <span class="cw-filter-group__arrow">▸</span></button>' +
          '<div class="cw-filter-group__body" style="display:none">' +
            '<label class="cw-filter-check"><input type="checkbox" value="included" class="cw-filter-input" data-filter="addon"><span>Fully included (≤$55)</span></label>' +
            '<label class="cw-filter-check"><input type="checkbox" value="addon" class="cw-filter-input" data-filter="addon"><span>Has add-on cost (>$55)</span></label>' +
          '</div></div>';

      if (filterGroupsEl) filterGroupsEl.innerHTML = html;

      // Expand all groups on desktop, keep collapsed on mobile
      var isDesktop = window.innerWidth > 768;
      if (isDesktop) {
        document.querySelectorAll('.cw-filter-group__toggle').forEach(function(btn) {
          btn.setAttribute('aria-expanded', 'true');
          var body = btn.nextElementSibling;
          if (body) body.style.display = '';
          var arrow = btn.querySelector('.cw-filter-group__arrow');
          if (arrow) arrow.textContent = '▾';
        });
      }

      // Wire checkboxes
      document.querySelectorAll('.cw-filter-input').forEach(function(cb) {
        cb.addEventListener('change', function() {
          var key = this.getAttribute('data-filter');
          if (!activeFilters[key]) activeFilters[key] = [];
          if (this.checked) { if (!activeFilters[key].includes(this.value)) activeFilters[key].push(this.value); }
          else { activeFilters[key] = activeFilters[key].filter(function(v){ return v !== cb.value; }); }
          applyAll();
        });
      });

      // Wire number range inputs
      ['speed','spin','control'].forEach(function(key) {
        var minEl = document.getElementById('cw-' + key + '-min');
        var maxEl = document.getElementById('cw-' + key + '-max');
        if (!minEl || !maxEl) return;
        function updateRange() {
          var loStr = minEl.value.trim();
          var hiStr = maxEl.value.trim();
          activeFilters[key + 'Min'] = loStr !== '' ? parseInt(loStr, 10) : 0;
          activeFilters[key + 'Max'] = hiStr !== '' ? parseInt(hiStr, 10) : 100;
          applyAll();
        }
        minEl.addEventListener('input', updateRange);
        maxEl.addEventListener('input', updateRange);
      });

      // Collapsible toggles
      document.querySelectorAll('.cw-filter-group__toggle').forEach(function(btn) {
        btn.addEventListener('click', function() {
          var exp = this.getAttribute('aria-expanded') === 'true';
          this.setAttribute('aria-expanded', String(!exp));
          var body = this.nextElementSibling;
          if (body) body.style.display = exp ? 'none' : '';
          var arrow = this.querySelector('.cw-filter-group__arrow');
          if (arrow) arrow.textContent = exp ? '▸' : '▾';
        });
      });
    }

    // ── APPLY ALL ─────────────────────────────────────────────────
    function applyAll() {
      var query = searchEl ? searchEl.value.toLowerCase().trim() : '';
      var visible = allRows.filter(function(row) {
        var title   = row.getAttribute('data-product-title') || '';
        var vendor  = row.getAttribute('data-vendor') || '';
        var rtype   = row.getAttribute('data-type')    || '';
        var rtech   = row.getAttribute('data-tech')    || '';
        var hard    = row.getAttribute('data-hardness')|| '';
        var speed   = parseInt(row.getAttribute('data-speed')  ||'0', 10);
        var spin    = parseInt(row.getAttribute('data-spin')   ||'0', 10);
        var ctrl    = parseInt(row.getAttribute('data-control')||'0', 10);
        var addonT  = row.getAttribute('data-addon-type') || '';

        if (query && !title.includes(query) && !vendor.includes(query)) return false;
        if (activeFilters.brand.length    && !activeFilters.brand.map(function(v){return v.toLowerCase();}).includes(vendor)) return false;
        if (activeFilters.type.length     && !activeFilters.type.map(function(v){return v.toLowerCase();}).includes(rtype))   return false;
        if (activeFilters.tech.length     && !activeFilters.tech.map(function(v){return v.toLowerCase();}).includes(rtech))   return false;
        if (activeFilters.hardness.length && !activeFilters.hardness.map(function(v){return v.toLowerCase();}).includes(hard)) return false;
        if (activeFilters.addon.length    && !activeFilters.addon.includes(addonT)) return false;
        // Sliders — only filter if product has a value AND it's outside range
        if (speed > 0 && (speed < activeFilters.speedMin || speed > activeFilters.speedMax)) return false;
        if (spin  > 0 && (spin  < activeFilters.spinMin  || spin  > activeFilters.spinMax))  return false;
        if (ctrl  > 0 && (ctrl  < activeFilters.controlMin|| ctrl > activeFilters.controlMax)) return false;
        return true;
      });

      if (sortCol) {
        var numCols = ['speed','spin','control','price','surcharge'];
        visible.sort(function(a, b) {
          var av = a.getAttribute('data-' + sortCol) || '';
          var bv = b.getAttribute('data-' + sortCol) || '';
          var cmp = numCols.includes(sortCol) ? (parseFloat(av)||0) - (parseFloat(bv)||0) : av.localeCompare(bv);
          return sortDir === 'desc' ? -cmp : cmp;
        });
      }

      // Re-append in sorted order, then hide filtered-out rows
      visible.forEach(function(r) { tbody.appendChild(r); });
      allRows.forEach(function(r) {
        r.style.display = visible.includes(r) ? '' : 'none';
      });
      if (countEl) countEl.textContent = visible.length;
      if (emptyEl) emptyEl.style.display = visible.length === 0 ? '' : 'none';
    }

    // ── SORT HEADERS ─────────────────────────────────────────────
    document.querySelectorAll('.cw-sortable').forEach(function(th) {
      th.addEventListener('click', function() {
        var col = this.getAttribute('data-col');
        sortDir = (sortCol === col) ? (sortDir === 'desc' ? 'asc' : 'desc') : 'desc';
        sortCol = col;
        document.querySelectorAll('.cw-sortable').forEach(function(t) {
          t.classList.remove('is-sorted-asc','is-sorted-desc');
          var ic = t.querySelector('.cw-sort-icon'); if (ic) ic.textContent = '↕';
        });
        this.classList.add('is-sorted-' + sortDir);
        var icon = this.querySelector('.cw-sort-icon'); if (icon) icon.textContent = sortDir === 'asc' ? '↑' : '↓';
        applyAll();
      });
    });

    // ── CLEAR ────────────────────────────────────────────────────
    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        document.querySelectorAll('.cw-filter-input').forEach(function(cb){ cb.checked = false; });
        document.querySelectorAll('.cw-num-input').forEach(function(r){ r.value = ''; });
        if (searchEl) searchEl.value = '';
        activeFilters = { brand: [], type: [], tech: [], hardness: [], addon: [], speedMin: 0, speedMax: 100, spinMin: 0, spinMax: 100, controlMin: 0, controlMax: 100 };
        applyAll();
      });
    }
    if (searchEl) searchEl.addEventListener('input', applyAll);

    // ── INIT ─────────────────────────────────────────────────────
    tbody.innerHTML = '';
    products.forEach(function(p) {
      var row = buildRow(p);
      allRows.push(row);
      tbody.appendChild(row);
    });
    buildFilters();
    if (countEl) countEl.textContent = products.length;
    applyAll();
  }

  // ─── Step 3 / 5: Rubber PDP — fetches /products/{handle}.js and builds ─────
  function initRubberPdp() {
    var mount = document.getElementById('cw-pdp-mount');
    if (!mount) return;

    var root = document.getElementById('combo-wizard-root');
    if (!root) return;

    var params = new URLSearchParams(window.location.search);
    var step = params.get('step');
    var rubberHandle = params.get('rubber_handle') || '';
    var rubberId = params.get('rubber_id') || '';
    var comboUrl = root.getAttribute('data-combo-url') || window.location.pathname;
    var comboHandle = root.getAttribute('data-combo-handle') || '';

    var isFhPdp = step === 'fh_pdp';
    var backStep = isFhPdp ? 'fh' : 'bh';

    // Prerequisite guards — can't PDP ahead of the needed prior selections
    var s = getState();
    if (isFhPdp && !s.bladeVariantId) {
      window.location.href = comboUrl + '?step=blade';
      return;
    }
    if (!isFhPdp && !s.fhVariantId) {
      window.location.href = comboUrl + '?step=fh';
      return;
    }

    if (!rubberHandle && !rubberId) {
      mount.innerHTML = buildPdpErrorHtml('No rubber specified.', comboUrl, backStep);
      return;
    }

    // Read specs (speed/spin/control/hardness/type/tech) from the same embedded
    // JSON the collection step uses — avoids a second network round-trip.
    var specs = {};
    var dataEl = document.getElementById('cw-all-rubber-data');
    if (dataEl) {
      try {
        var allRubberData = JSON.parse(dataEl.textContent);
        specs = allRubberData.find(function(p) {
          return (rubberHandle && p.handle === rubberHandle) || (rubberId && String(p.id) === String(rubberId));
        }) || {};
      } catch (e) {}
    }

    var fetchHandle = rubberHandle || specs.handle;
    if (!fetchHandle) {
      mount.innerHTML = buildPdpErrorHtml('Could not identify the selected rubber.', comboUrl, backStep);
      return;
    }

    // Fetch live product data from Shopify's built-in product JSON endpoint.
    // This bypasses the theme/page cache entirely — always returns current data.
    fetch('/products/' + encodeURIComponent(fetchHandle) + '.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function(r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function(product) {
        mount.innerHTML = buildPdpHtml(product, specs, step, comboUrl, comboHandle);
        wirePdpInteractions(product, step, comboUrl, comboHandle);
        renderMiniSummary();
      })
      .catch(function(err) {
        console.error('[CW] PDP fetch failed:', err);
        mount.innerHTML = buildPdpErrorHtml('Could not load rubber details.', comboUrl, backStep);
      });
  }

  // ── PDP helpers ───────────────────────────────────────────────────────────
  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function buildPdpErrorHtml(message, comboUrl, backStep) {
    return '<div class="cw-error__inner" style="padding:2rem;text-align:center;">' +
      '<p>' + escapeAttr(message) + ' ' +
      '<a href="' + escapeAttr(comboUrl) + '?step=' + escapeAttr(backStep) + '">Go back and choose a rubber.</a></p>' +
      '</div>';
  }

  function buildPdpHtml(product, specs, step, comboUrl, comboHandle) {
    var isFhPdp = step === 'fh_pdp';
    var backStep = isFhPdp ? 'fh' : 'bh';
    var firstVariant = (product.variants || []).find(function(v) { return v.available; }) || product.variants[0] || {};
    var fvPrice = firstVariant.price || 0;
    var fvSurcharge = Math.max(0, fvPrice - ALLOWANCE_CENTS);

    // ── Media (main image + optional thumbnail strip) ──
    var mediaHtml = '';
    if (product.featured_image) {
      mediaHtml += '<img class="cw-rubber-pdp__img" src="' + escapeAttr(product.featured_image) + '" alt="' + escapeAttr(product.title) + '" loading="eager">';
    }
    if (product.images && product.images.length > 1) {
      mediaHtml += '<div class="cw-rubber-pdp__thumbs">';
      product.images.slice(0, 5).forEach(function(src, idx) {
        mediaHtml += '<button class="cw-rubber-pdp__thumb' + (idx === 0 ? ' is-active' : '') + '" data-src="' + escapeAttr(src) + '" type="button">' +
          '<img src="' + escapeAttr(src) + '" loading="lazy" alt="' + escapeAttr(product.title) + '">' +
          '</button>';
      });
      mediaHtml += '</div>';
    }

    // ── Pricing block ──
    var pricingHtml = '<div class="cw-rubber-pdp__pricing" id="cw-pdp-pricing">' +
      '<div class="cw-pdp-price">' +
        '<span class="cw-pdp-price__retail">Retail: <strong id="cw-pdp-retail">' + formatMoney(fvPrice) + '</strong></span>' +
        (fvSurcharge > 0
          ? '<span class="cw-pdp-price__addon">Add-on: <strong id="cw-pdp-addon">' + formatMoney(fvSurcharge) + '</strong></span>'
          : '<span class="cw-pdp-price__included" id="cw-pdp-included">✓ Fully included in combo</span>'
        ) +
      '</div>' +
      '<p class="cw-pdp-price__note">Rubbers up to $55 are included. You only pay the difference above $55.</p>' +
      '</div>';

    // ── Inline specs panel (Type · Tech, Hardness, Speed/Spin/Control bars) ──
    var hasAnySpec = specs.speed || specs.spin || specs.control || specs.hardness || specs.rubber_type;
    var specsHtml = '';
    if (hasAnySpec) {
      specsHtml = '<div class="cw-pdp-specs-inline">';
      if (specs.rubber_type) {
        specsHtml += '<div class="cw-pdp-spec-row"><span class="cw-pdp-spec-label">Type</span><span class="cw-pdp-spec-value">' +
          escapeAttr(specs.rubber_type) + (specs.rubber_tech ? ' · ' + escapeAttr(specs.rubber_tech) : '') + '</span></div>';
      }
      if (specs.hardness) {
        specsHtml += '<div class="cw-pdp-spec-row"><span class="cw-pdp-spec-label">Hardness</span><span class="cw-pdp-spec-value">' + escapeAttr(specs.hardness) + '</span></div>';
      }
      [['speed', 'Speed'], ['spin', 'Spin'], ['control', 'Control']].forEach(function(pair) {
        var key = pair[0], label = pair[1];
        if (specs[key]) {
          specsHtml += '<div class="cw-pdp-spec-row cw-pdp-spec-row--stat">' +
            '<span class="cw-pdp-spec-label">' + label + '</span>' +
            '<div class="cw-pdp-spec-bar-wrap">' +
              '<div class="cw-pdp-spec-bar"><div class="cw-pdp-spec-fill" style="width:' + specs[key] + '%"></div></div>' +
              '<span class="cw-pdp-spec-num">' + specs[key] + '</span>' +
            '</div>' +
          '</div>';
        }
      });
      specsHtml += '</div>';
    }

    // ── Variants ──
    var variantsHtml = '';
    var variants = product.variants || [];
    var isSingleDefault = variants.length === 1 && (variants[0].title === 'Default Title' || variants[0].title === 'Default');

    if (isSingleDefault) {
      variantsHtml = '<input type="hidden" name="variant_id" value="' + variants[0].id + '" data-variant-title="Default" data-price="' + variants[0].price + '">';
    } else {
      variantsHtml += '<div class="cw-variants" id="cw-rubber-variants">';
      (product.options || []).forEach(function(opt, optIdx) {
        var position = optIdx + 1;
        // Shopify's /products/{handle}.js returns options as either strings
        // ("Color") or objects ({name:"Color", values:[...], position:1}).
        var optLabel = (typeof opt === 'string')
          ? opt
          : (opt && opt.name) || ('Option ' + position);
        var values = [];
        // Prefer values from the option object if available; otherwise derive
        // from variants (covers both shapes).
        if (opt && typeof opt === 'object' && Array.isArray(opt.values)) {
          values = opt.values.slice();
        } else {
          variants.forEach(function(v) {
            var val = v['option' + position];
            if (val && values.indexOf(val) === -1) values.push(val);
          });
        }
        if (!values.length) return;
        variantsHtml += '<div class="cw-variants__group">';
        variantsHtml += '<p class="cw-variants__label">' + escapeAttr(optLabel) + ':</p>';
        variantsHtml += '<div class="cw-variants__grid">';
        values.forEach(function(val, idx) {
          variantsHtml += '<label class="cw-variant-tile' + (idx === 0 ? ' is-selected' : '') + '">' +
            '<input type="radio" name="option_' + position + '" value="' + escapeAttr(val) + '"' + (idx === 0 ? ' checked' : '') + '>' +
            '<div class="cw-variant-tile__inner"><span class="cw-variant-tile__name">' + escapeAttr(val) + '</span></div>' +
            '</label>';
        });
        variantsHtml += '</div></div>';
      });
      variantsHtml += '<input type="hidden" name="variant_id" id="cw-rubber-variant-id" value="' + firstVariant.id + '">';
      variantsHtml += '</div>';

      // Embed variants JSON for the option-picker logic below to consume
      var variantsPayload = variants.map(function(v) {
        return {
          id: v.id, title: v.title, price: v.price, available: v.available,
          option1: v.option1, option2: v.option2, option3: v.option3
        };
      });
      variantsHtml += '<script type="application/json" id="cw-rubber-variants-json">' +
        JSON.stringify(variantsPayload).replace(/<\//g, '<\\/') +
        '<' + '/script>';
    }

    // ── Description (pre-rendered HTML from Shopify) ──
    var descHtml = product.description ? '<div class="cw-rubber-pdp__desc rte">' + product.description + '</div>' : '';

    var continueLabel = isFhPdp ? 'Continue to Backhand Rubber →' : 'Continue to Review →';

    return '<div class="cw-rubber-pdp cw-rubber-pdp--2col">' +
      '<div class="cw-rubber-pdp__media">' + mediaHtml + '</div>' +
      '<div class="cw-rubber-pdp__info">' +
        '<p class="cw-rubber-pdp__vendor">' + escapeAttr(product.vendor || '') + '</p>' +
        '<h2 class="cw-rubber-pdp__name">' + escapeAttr(product.title || '') + '</h2>' +
        pricingHtml +
        specsHtml +
        '<form class="cw-rubber-pdp__form" id="cw-rubber-pdp-form" ' +
          'data-combo-handle="' + escapeAttr(comboHandle) + '" ' +
          'data-rubber-product-id="' + product.id + '" ' +
          'data-rubber-product-title="' + escapeAttr(product.title || '') + '" ' +
          'data-rubber-handle="' + escapeAttr(product.handle || '') + '" ' +
          'data-step="' + escapeAttr(step) + '" ' +
          'data-base-url="' + escapeAttr(comboUrl) + '">' +
          variantsHtml +
          descHtml +
          '<div class="cw-step__actions">' +
            '<a href="' + escapeAttr(comboUrl) + '?step=' + escapeAttr(backStep) + '" class="cw-btn cw-btn--outline">← Back</a>' +
            '<button type="submit" class="cw-btn cw-btn--primary" id="cw-rubber-pdp-submit">' + continueLabel + '</button>' +
          '</div>' +
        '</form>' +
      '</div>' +
    '</div>';
  }

  function wirePdpInteractions(product, step, comboUrl, comboHandle) {
    var form = document.getElementById('cw-rubber-pdp-form');
    if (!form) return;

    var rubberProductId = form.getAttribute('data-rubber-product-id');
    var rubberProductTitle = form.getAttribute('data-rubber-product-title');
    var rubberHandle = form.getAttribute('data-rubber-handle') || '';

    // ── Variant tile highlighting on freshly mounted radios ──
    form.querySelectorAll('.cw-variant-tile input[type="radio"]').forEach(function (radio) {
      if (radio.checked) radio.closest('.cw-variant-tile').classList.add('is-selected');
      radio.addEventListener('change', function () {
        var group = this.closest('.cw-variants__grid');
        if (!group) return;
        group.querySelectorAll('.cw-variant-tile').forEach(function (tile) {
          tile.classList.remove('is-selected');
        });
        this.closest('.cw-variant-tile').classList.add('is-selected');
      });
    });

    // ── Thumbnail gallery ──
    var mainImg = document.querySelector('.cw-rubber-pdp__img');
    document.querySelectorAll('.cw-rubber-pdp__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        document.querySelectorAll('.cw-rubber-pdp__thumb').forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
        if (mainImg) mainImg.src = this.getAttribute('data-src');
      });
    });

    // ── Multi-option variant picker + live pricing ──
    var variantsJsonEl = document.getElementById('cw-rubber-variants-json');
    var variantIdInput = document.getElementById('cw-rubber-variant-id');

    if (variantsJsonEl && variantIdInput) {
      var allVariants = [];
      try { allVariants = JSON.parse(variantsJsonEl.textContent); } catch (e) {}

      function getSelectedOptions() {
        var opts = {};
        form.querySelectorAll('[name^="option_"]').forEach(function (radio) {
          if (radio.checked) {
            var pos = radio.name.replace('option_', '');
            opts['option' + pos] = radio.value;
          }
        });
        return opts;
      }

      function findVariant(selectedOpts) {
        return allVariants.find(function (v) {
          return Object.keys(selectedOpts).every(function (key) {
            return v[key] === selectedOpts[key];
          });
        }) || null;
      }

      function updatePricing(variant) {
        if (!variant) return;
        var price = variant.price;
        var surcharge = surchargeFromCents(price);

        var priceLineEl = document.querySelector('#cw-pdp-pricing .cw-pdp-price');
        if (priceLineEl) {
          priceLineEl.innerHTML =
            '<span class="cw-pdp-price__retail">Retail: <strong id="cw-pdp-retail">' + formatMoney(price) + '</strong></span>' +
            (surcharge > 0
              ? '<span class="cw-pdp-price__addon">Add-on: <strong id="cw-pdp-addon">' + formatMoney(surcharge) + '</strong></span>'
              : '<span class="cw-pdp-price__included" id="cw-pdp-included">✓ Fully included in combo</span>'
            );
        }

        var submitBtn = document.getElementById('cw-rubber-pdp-submit');
        if (submitBtn) {
          if (variant.available === false) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Sold Out';
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = step === 'fh_pdp' ? 'Continue to Backhand Rubber →' : 'Continue to Review →';
          }
        }
      }

      function onOptionChange() {
        var selected = getSelectedOptions();
        var variant = findVariant(selected);
        if (variant) {
          variantIdInput.value = variant.id;
          updatePricing(variant);
        }
      }

      form.querySelectorAll('[name^="option_"]').forEach(function (radio) {
        radio.addEventListener('change', onOptionChange);
      });

      onOptionChange();
    }

    // ── Form submit: save to state, navigate to next step ──
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var variantInput = form.querySelector('input[name="variant_id"]');
      if (!variantInput || !variantInput.value) {
        alert('Please select a variant.');
        return;
      }

      var variantId = variantInput.value;
      var selectedVariant = null;
      if (variantsJsonEl) {
        try {
          var vs = JSON.parse(variantsJsonEl.textContent);
          selectedVariant = vs.find(function (v) { return String(v.id) === String(variantId); });
        } catch (e) {}
      }
      if (!selectedVariant) {
        selectedVariant = {
          id: variantId,
          title: variantInput.getAttribute('data-variant-title') || 'Default',
          price: parseInt(variantInput.getAttribute('data-price') || '0', 10)
        };
      }

      var priceCents = selectedVariant.price || 0;
      var surchargeCents = surchargeFromCents(priceCents);

      if (step === 'fh_pdp') {
        mergeState({
          fhProductId: rubberProductId,
          fhHandle: rubberHandle,
          fhTitle: rubberProductTitle,
          fhVariantId: String(variantId),
          fhVariantTitle: selectedVariant.title || '',
          fhPriceCents: priceCents,
          fhSurchargeCents: surchargeCents,
          // Clear BH when FH changes — it may no longer be compatible
          bhProductId: null, bhHandle: null, bhTitle: null,
          bhVariantId: null, bhVariantTitle: null,
          bhPriceCents: null, bhSurchargeCents: null
        });
        window.location.href = comboUrl + '?step=bh&position=bh';
      } else {
        mergeState({
          bhProductId: rubberProductId,
          bhHandle: rubberHandle,
          bhTitle: rubberProductTitle,
          bhVariantId: String(variantId),
          bhVariantTitle: selectedVariant.title || '',
          bhPriceCents: priceCents,
          bhSurchargeCents: surchargeCents
        });
        window.location.href = comboUrl + '?step=review';
      }
    });
  }

  // ─── Step 6: Review ───────────────────────────────────────────────────────
  function initReview() {
    var root = document.getElementById('cw-review-root');
    if (!root) return;

    var s = getState();
    var comboVariantId = root.getAttribute('data-combo-variant-id');
    var comboTitle = root.getAttribute('data-combo-title');

    // Validate completeness
    if (!s.bladeVariantId || !s.fhVariantId || !s.bhVariantId) {
      var comboHandle = new URLSearchParams(window.location.search).get('combo') || '';
      document.getElementById('cw-review-selections').innerHTML =
        '<div class="cw-error__inner"><p>Your combo is incomplete. <a href="' + window.location.pathname + '?step=blade">Start over from the blade step.</a></p></div>';
      return;
    }

    var fhSurcharge = Number(s.fhSurchargeCents || 0);
    var bhSurcharge = Number(s.bhSurchargeCents || 0);
    var totalSurcharge = fhSurcharge + bhSurcharge;

    // ── ITTF Color Rule Check ─────────────────────────────────────────────
    // One rubber must be black, the other must be a different color.
    // Variant titles are formatted as "COLOR / thickness" e.g. "BLACK / 2.0mm"
    function extractColor(variantTitle) {
      if (!variantTitle || variantTitle === 'Default') return null;
      // Color is everything before the first ' / ' or the whole string if no separator
      var parts = variantTitle.split(' / ');
      return parts[0].trim().toUpperCase();
    }

    var fhColor = extractColor(s.fhVariantTitle);
    var bhColor = extractColor(s.bhVariantTitle);

    function checkIttfRule(colorA, colorB) {
      if (!colorA || !colorB) return null; // can't determine
      var aIsBlack = colorA === 'BLACK';
      var bIsBlack = colorB === 'BLACK';
      if (aIsBlack && bIsBlack) return 'both-black';
      if (!aIsBlack && !bIsBlack) return 'neither-black';
      return 'valid';
    }

    var ittfStatus = checkIttfRule(fhColor, bhColor);

    // Render ITTF warning banner if needed
    var warningEl = document.getElementById('cw-ittf-warning');
    if (warningEl) {
      if (ittfStatus === 'both-black') {
        warningEl.innerHTML =
          '<div class="cw-ittf-warning cw-ittf-warning--error">' +
          '<span class="cw-ittf-warning__icon">⚠️</span>' +
          '<div class="cw-ittf-warning__body">' +
          '<strong>Not ITTF Legal:</strong> Both rubbers are black. ' +
          'For competitive play, one rubber must be black and the other must be a different color (red, blue, etc.). ' +
          '<a href="#" class="cw-ittf-warning__link" id="cw-ittf-change-bh">Change Backhand Rubber →</a>' +
          '</div></div>';
        warningEl.style.display = '';
        var changeBhLink = document.getElementById('cw-ittf-change-bh');
        if (changeBhLink) {
          changeBhLink.addEventListener('click', function(e) {
            e.preventDefault();
            var comboH = new URLSearchParams(window.location.search).get('combo') || s.comboHandle || '';
            var bUrl = root.getAttribute('data-base-url') || window.location.pathname;
            // Go to BH rubber PDP if one is already selected, otherwise BH collection
            if (s.bhProductId) {
              window.location.href = bUrl + '?step=bh_pdp&position=bh&rubber_id=' + s.bhProductId + (s.bhHandle ? '&rubber_handle=' + encodeURIComponent(s.bhHandle) : '');
            } else {
              window.location.href = bUrl + '?step=bh&position=bh';
            }
          });
        }
      } else if (ittfStatus === 'neither-black') {
        warningEl.innerHTML =
          '<div class="cw-ittf-warning cw-ittf-warning--error">' +
          '<span class="cw-ittf-warning__icon">⚠️</span>' +
          '<div class="cw-ittf-warning__body">' +
          '<strong>Not ITTF Legal:</strong> Neither rubber is black (Forehand: ' + (fhColor || '?') + ', Backhand: ' + (bhColor || '?') + '). ' +
          'For competitive play, one rubber must be black and the other a different color. ' +
          '<a href="#" class="cw-ittf-warning__link" id="cw-ittf-change-bh">Change Backhand Rubber →</a>' +
          '</div></div>';
        warningEl.style.display = '';
        var changeBhLink2 = document.getElementById('cw-ittf-change-bh');
        if (changeBhLink2) {
          changeBhLink2.addEventListener('click', function(e) {
            e.preventDefault();
            var comboH = new URLSearchParams(window.location.search).get('combo') || s.comboHandle || '';
            var bUrl = root.getAttribute('data-base-url') || window.location.pathname;
            if (s.bhProductId) {
              window.location.href = bUrl + '?step=bh_pdp&position=bh&rubber_id=' + s.bhProductId + (s.bhHandle ? '&rubber_handle=' + encodeURIComponent(s.bhHandle) : '');
            } else {
              window.location.href = bUrl + '?step=bh&position=bh';
            }
          });
        }
      } else {
        warningEl.style.display = 'none';
      }
    }

    // Render selections — with thumbnails, no "included" text unless there's an add-on
    var selEl = document.getElementById('cw-review-selections');
    if (selEl) {
      function reviewCard(label, title, variantTitle, meta, imgUrl) {
        var imgHtml = imgUrl
          ? '<img src="' + imgUrl + '" alt="' + title + '" class="cw-review__card-thumb">'
          : '<div class="cw-review__card-thumb cw-review__card-thumb--empty"></div>';
        var variantStr = (variantTitle && variantTitle !== 'Default') ? ' <span class="cw-review__card-variant">— ' + variantTitle + '</span>' : '';
        var metaHtml = meta ? '<p class="cw-review__card-meta">' + meta + '</p>' : '';
        return [
          '<div class="cw-review__card">',
          '  <div class="cw-review__card-img">' + imgHtml + '</div>',
          '  <div class="cw-review__card-body">',
          '    <p class="cw-review__card-label">' + label + '</p>',
          '    <p class="cw-review__card-value">' + title + variantStr + '</p>',
          metaHtml,
          '  </div>',
          '</div>'
        ].join('');
      }

      // Fetch product images via Shopify product JSON
      function getProductImg(handle, cb) {
        if (!handle) { cb(null); return; }
        fetch('/products/' + handle + '.js')
          .then(function(r){ return r.json(); })
          .then(function(p){ cb(p.featured_image ? p.featured_image : null); })
          .catch(function(){ cb(null); });
      }

      // Build cards once we have images
      function buildReviewCards(comboImg, bladeImg, fhImg, bhImg) {
        var cards = '';
        cards += reviewCard('Combo Special', comboTitle, '', '', comboImg);
        // Blade — no meta (blade is always included, no add-on)
        cards += reviewCard('Blade', s.bladeTitle || '', s.bladeVariantTitle, '', bladeImg);
        // FH rubber — meta only if surcharge
        var fhMeta = fhSurcharge > 0 ? '+' + formatMoney(fhSurcharge) + ' add-on' : '';
        cards += reviewCard('Forehand Rubber', s.fhTitle || '', s.fhVariantTitle, fhMeta, fhImg);
        // BH rubber — meta only if surcharge
        var bhMeta = bhSurcharge > 0 ? '+' + formatMoney(bhSurcharge) + ' add-on' : '';
        cards += reviewCard('Backhand Rubber', s.bhTitle || '', s.bhVariantTitle, bhMeta, bhImg);
        selEl.innerHTML = cards;
      }

      // Load images in parallel then render (4 images: combo, blade, fh, bh)
      var comboImg = null, bladeImg = null, fhImg = null, bhImg = null, loaded = 0;
      function onImgLoaded() {
        loaded++;
        if (loaded >= 4) buildReviewCards(comboImg, bladeImg, fhImg, bhImg);
      }
      getProductImg(s.comboHandle, function(img){ comboImg = img; onImgLoaded(); });
      getProductImg(s.bladeHandle, function(img){ bladeImg = img; onImgLoaded(); });
      getProductImg(s.fhHandle, function(img){ fhImg = img; onImgLoaded(); });
      getProductImg(s.bhHandle, function(img){ bhImg = img; onImgLoaded(); });
    }

    // Render pricing
    var pricingEl = document.getElementById('cw-review-pricing');
    if (pricingEl) {
      pricingEl.style.display = '';
      var fhAddonEl = document.getElementById('cw-review-fh-addon');
      var bhAddonEl = document.getElementById('cw-review-bh-addon');
      var totalAddonEl = document.getElementById('cw-review-total-addon');
      if (fhAddonEl) fhAddonEl.textContent = fhSurcharge > 0 ? '+' + formatMoney(fhSurcharge) : 'Included';
      if (bhAddonEl) bhAddonEl.textContent = bhSurcharge > 0 ? '+' + formatMoney(bhSurcharge) : 'Included';
      var comboPriceCents = parseInt(root.getAttribute('data-combo-price'), 10) || 0;
      var comboPriceEl = document.getElementById('cw-review-combo-price');
      if (comboPriceEl) comboPriceEl.textContent = formatMoney(comboPriceCents);
      if (totalAddonEl) totalAddonEl.textContent = formatMoney(comboPriceCents + totalSurcharge);
    }

    // Back button: go back to BH collection but restore BH selection
    var backBtn = document.getElementById('cw-review-back');
    if (backBtn) {
      var comboHandle = new URLSearchParams(window.location.search).get('combo') || s.comboHandle || '';
      // We want to go back to the BH PDP if we have a BH product selected
      if (s.bhProductId) {
        var revBaseUrl = root.getAttribute('data-base-url') || window.location.pathname;
      backBtn.href = revBaseUrl + '?step=bh_pdp&position=bh&rubber_id=' + s.bhProductId;
      } else {
        backBtn.href = revBaseUrl + '?step=bh&position=bh';
      }
    }

    // Form submit
    var form = document.getElementById('cw-review-form');
    var submitBtn = document.getElementById('cw-review-submit');
    var addingEl = document.getElementById('cw-review-adding');
    var errorEl = document.getElementById('cw-review-error');

    if (!form) return;

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      var assemblyInput = form.querySelector('input[name="paddle_assembly"]:checked');
      if (!assemblyInput) {
        if (errorEl) { errorEl.textContent = 'Please select a paddle assembly option.'; errorEl.style.display = ''; }
        return;
      }

      // ITTF warning on submit — if illegal combo, confirm before proceeding
      if (ittfStatus === 'both-black' || ittfStatus === 'neither-black') {
        var ruleMsg = ittfStatus === 'both-black'
          ? 'Both rubbers are black, which is not legal for ITTF competitive play.'
          : 'Neither rubber is black, which is not legal for ITTF competitive play.';
        var confirmed = window.confirm(
          ruleMsg + '\n\nFor competitive/tournament play, one rubber must be black and the other a different color.\n\nDo you still want to add this paddle to your cart?'
        );
        if (!confirmed) return;
      }

      if (submitBtn) submitBtn.disabled = true;
      if (addingEl) addingEl.style.display = '';
      if (errorEl) errorEl.style.display = 'none';

      var comboGroupId = 'combo-' + Date.now();
      var assemblyValue = assemblyInput.value;

      // Per-line unique tokens: without these, Shopify may merge a combo
      // child's line with a same-variant standalone item already in the cart,
      // causing SupaEasy's discount function to (correctly, by its rules)
      // apply combo pricing to items the customer added individually. Each
      // child gets a distinct `_combo_line_id` so every combo child is a
      // guaranteed-unique cart line.
      function uniqueToken() {
        return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }

      var parentProperties = {
        'Paddle Assembly': assemblyValue,
        '_assembly_answered': 'true',
        'Blade': (s.bladeTitle || '') + (s.bladeVariantTitle && s.bladeVariantTitle !== 'Default' ? ' — ' + s.bladeVariantTitle : ''),
        'Forehand Rubber': (s.fhTitle || '') + (s.fhVariantTitle && s.fhVariantTitle !== 'Default' ? ' — ' + s.fhVariantTitle : ''),
        'Backhand Rubber': (s.bhTitle || '') + (s.bhVariantTitle && s.bhVariantTitle !== 'Default' ? ' — ' + s.bhVariantTitle : ''),
        '_combo_group': comboGroupId
      };

      if (fhSurcharge > 0) parentProperties['Forehand Add-On'] = formatMoney(fhSurcharge);
      if (bhSurcharge > 0) parentProperties['Backhand Add-On'] = formatMoney(bhSurcharge);
      if (totalSurcharge > 0) parentProperties['Total Add-On'] = formatMoney(totalSurcharge);

      var childProps = { '_combo_group': comboGroupId, '_combo_child': 'true' };

      var items = [
        {
          id: Number(comboVariantId),
          quantity: 1,
          properties: parentProperties
        },
        {
          id: Number(s.bladeVariantId),
          quantity: 1,
          properties: Object.assign({}, childProps, {
            '_combo_role': 'blade',
            '_combo_surcharge': '0',
            '_combo_line_id': uniqueToken()
          })
        },
        {
          id: Number(s.fhVariantId),
          quantity: 1,
          properties: Object.assign({}, childProps, {
            '_combo_role': 'fh_rubber',
            '_combo_surcharge': String(fhSurcharge),
            '_combo_line_id': uniqueToken()
          })
        },
        {
          id: Number(s.bhVariantId),
          quantity: 1,
          properties: Object.assign({}, childProps, {
            '_combo_role': 'bh_rubber',
            '_combo_surcharge': String(bhSurcharge),
            '_combo_line_id': uniqueToken()
          })
        }
      ];

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          var errData = await response.json().catch(function () { return {}; });
          throw new Error(errData.description || 'Could not add combo to cart. Please try again.');
        }

        clearState();
        window.location.href = '/cart';

      } catch (err) {
        if (submitBtn) submitBtn.disabled = false;
        if (addingEl) addingEl.style.display = 'none';
        if (errorEl) {
          errorEl.textContent = err.message || 'Something went wrong. Please try again.';
          errorEl.style.display = '';
        }
      }
    });
  }

  // ─── Wizard router — the v129 entry point ───────────────────────────────
  //
  // Reads window.location.search to determine which step to show, reveals
  // the matching step-container, hides the rest, and dispatches to the
  // appropriate per-step init function. This is all client-side — the
  // server renders the same HTML regardless of ?step=X, so the cache can
  // never serve the wrong step.
  //
  function initWizard() {
    var root = document.getElementById('combo-wizard-root');
    if (!root) return; // not on a wizard page, nothing to do

    var params = new URLSearchParams(window.location.search);
    var step = params.get('step') || 'blade';

    // Map step → container key (FH and BH share one container; PDPs share one)
    var containerKey;
    switch (step) {
      case 'fh':
      case 'bh':
        containerKey = 'rubber-select';
        break;
      case 'fh_pdp':
      case 'bh_pdp':
        containerKey = 'rubber-pdp';
        break;
      case 'review':
        containerKey = 'review';
        break;
      case 'blade':
      default:
        containerKey = 'blade';
        break;
    }

    // Reveal the active container, hide the others
    document.querySelectorAll('.cw-step-container').forEach(function(c) {
      if (c.getAttribute('data-cw-step') === containerKey) {
        c.removeAttribute('hidden');
      } else {
        c.setAttribute('hidden', '');
      }
    });

    // Update progress-bar active/done state
    updateProgressBar(step);

    // Update the dynamic step-header text on shared containers
    if (containerKey === 'rubber-select') {
      var isFh = step === 'fh';
      var numEl = document.getElementById('cw-rubber-step-num');
      var sideEl = document.getElementById('cw-rubber-step-side');
      if (numEl)  numEl.textContent  = isFh ? '2' : '3';
      if (sideEl) sideEl.textContent = isFh ? 'Forehand' : 'Backhand';

      // "Back to" link varies by whether we're on FH or BH
      var backLink = document.getElementById('cw-rubber-back-link');
      if (backLink) {
        var comboUrl = root.getAttribute('data-combo-url') || window.location.pathname;
        if (isFh) {
          backLink.href = comboUrl + '?step=blade';
          backLink.textContent = '← Back to Blade';
        } else {
          backLink.href = comboUrl + '?step=fh';
          backLink.textContent = '← Back to Forehand Rubber';
        }
      }
    } else if (containerKey === 'rubber-pdp') {
      var isFhPdp = step === 'fh_pdp';
      var pdpNumEl = document.getElementById('cw-pdp-step-num');
      var pdpSideEl = document.getElementById('cw-pdp-step-side');
      if (pdpNumEl)  pdpNumEl.textContent  = isFhPdp ? '2' : '3';
      if (pdpSideEl) pdpSideEl.textContent = isFhPdp ? 'Forehand' : 'Backhand';
    }

    // Render mini-summary (uses sessionStorage state)
    renderMiniSummary();

    // Variant tile highlighting for statically rendered controls (blade form)
    initVariantTileHighlight();

    // Per-step init
    switch (containerKey) {
      case 'blade':         initBladeForm();        break;
      case 'rubber-select': initRubberCollection(); break;
      case 'rubber-pdp':    initRubberPdp();        break;
      case 'review':        initReview();           break;
    }
  }

  function updateProgressBar(step) {
    var stage = ({
      blade:  'blade',
      fh:     'fh',   fh_pdp: 'fh',
      bh:     'bh',   bh_pdp: 'bh',
      review: 'review'
    })[step] || 'blade';

    var order = ['blade', 'fh', 'bh', 'review'];
    var currentIdx = order.indexOf(stage);

    document.querySelectorAll('.cw-progress__step').forEach(function(el) {
      var key = el.getAttribute('data-progress');
      var idx = order.indexOf(key);
      el.classList.remove('is-active', 'is-done');
      if (idx < currentIdx) el.classList.add('is-done');
      if (idx === currentIdx) el.classList.add('is-active');
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', initWizard);

  // bfcache restore: if user back-buttons from the cart, the page may restore
  // from the browser's back-forward cache without running DOMContentLoaded.
  // Re-run the router so sessionStorage guards and step routing apply.
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) initWizard();
  });

})();